import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { clientService } from '../../../services/clientService';

const ACTIVITY_CONFIG = {
  task_completed: { icon: 'CheckCircle2', color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'Task Completed', border: 'border-emerald-200' },
  snag_created: { icon: 'AlertOctagon', color: 'text-amber-600', bg: 'bg-amber-100', label: 'Snag Raised', border: 'border-amber-200' },
  snag_resolved: { icon: 'CheckSquare', color: 'text-blue-600', bg: 'bg-blue-100', label: 'Snag Resolved', border: 'border-blue-200' },
  snag_updated: { icon: 'Edit3', color: 'text-slate-600', bg: 'bg-slate-100', label: 'Snag Updated', border: 'border-slate-200' },
  incident_created: { icon: 'ShieldAlert', color: 'text-red-600', bg: 'bg-red-100', label: 'Incident Reported', border: 'border-red-200' },
  document_uploaded: { icon: 'FileUp', color: 'text-purple-600', bg: 'bg-purple-100', label: 'Document Uploaded', border: 'border-purple-200' },
  document_expiring: { icon: 'Clock', color: 'text-orange-600', bg: 'bg-orange-100', label: 'Document Expiring', border: 'border-orange-200' },
  site_created: { icon: 'Building2', color: 'text-accent', bg: 'bg-accent/10', label: 'Site Created', border: 'border-accent/20' },
  site_updated: { icon: 'Settings', color: 'text-slate-600', bg: 'bg-slate-100', label: 'Site Updated', border: 'border-slate-200' },
  worker_assigned: { icon: 'UserPlus', color: 'text-teal-600', bg: 'bg-teal-100', label: 'Worker Assigned', border: 'border-teal-200' },
  default: { icon: 'Activity', color: 'text-muted-foreground', bg: 'bg-muted', label: 'Activity', border: 'border-border' }
};

const EVENT_FILTERS = [
  { value: 'all', label: 'All Events', icon: 'LayoutList', types: null },
  { value: 'tasks', label: 'Tasks', icon: 'CheckCircle2', types: ['task_completed'] },
  { value: 'snags', label: 'Snags', icon: 'AlertOctagon', types: ['snag_created', 'snag_updated', 'snag_resolved'] },
  { value: 'incidents', label: 'Incidents', icon: 'ShieldAlert', types: ['incident_created'] },
  { value: 'documents', label: 'Documents', icon: 'FileUp', types: ['document_uploaded', 'document_expiring'] },
];

const formatRelativeTime = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const groupByDate = (activities) => {
  const groups = {};
  activities?.forEach(a => {
    const dateKey = new Date(a?.created_at)?.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    if (!groups?.[dateKey]) groups[dateKey] = [];
    groups?.[dateKey]?.push(a);
  });
  return groups;
};

const ClientActivityTimeline = ({ siteId, siteName }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [eventFilter, setEventFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (siteId) loadActivities();
  }, [siteId, eventFilter, dateFrom, dateTo]);

  const loadActivities = async () => {
    setLoading(true);
    setError('');
    try {
      const activeFilter = EVENT_FILTERS?.find(f => f?.value === eventFilter);
      const filters = {
        activityType: activeFilter?.types || 'all',
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      };
      const { data, error: err } = await clientService?.getSiteActivity(siteId, filters);
      if (err) throw err;
      setActivities(data || []);
    } catch (err) {
      console.error('[ClientActivityTimeline] load activity failed:', { code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301') {
        setError(`Permission denied loading activity: ${err?.message}`);
      } else {
        setError(`Failed to load activity: ${err?.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const grouped = groupByDate(activities);
  const hasFilters = eventFilter !== 'all' || dateFrom || dateTo;

  return (
    <div className="bg-card rounded-xl border border-border">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="Activity" size={18} className="text-accent" />
          <h3 className="font-semibold text-foreground">Activity Timeline</h3>
          {activities?.length > 0 && (
            <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">{activities?.length}</span>
          )}
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            showFilters || hasFilters ? 'bg-accent/10 text-accent' : 'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name="SlidersHorizontal" size={14} />
          Filters
          {hasFilters && <span className="w-1.5 h-1.5 rounded-full bg-accent" />}
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="px-5 py-4 border-b border-border bg-muted/20">
          {/* Event type pills */}
          <div className="flex flex-wrap gap-2 mb-3">
            {EVENT_FILTERS?.map(f => (
              <button
                key={f?.value}
                onClick={() => setEventFilter(f?.value)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  eventFilter === f?.value
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                <Icon name={f?.icon} size={12} />
                {f?.label}
              </button>
            ))}
          </div>
          {/* Date range */}
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground font-medium">From:</label>
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e?.target?.value)}
                className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground font-medium">To:</label>
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e?.target?.value)}
                className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
            {hasFilters && (
              <button
                onClick={() => { setEventFilter('all'); setDateFrom(''); setDateTo(''); }}
                className="text-xs text-muted-foreground hover:text-foreground underline"
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-5">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4]?.map(i => (
              <div key={i} className="flex gap-3 p-4 rounded-xl border border-border animate-pulse">
                <div className="w-9 h-9 rounded-full bg-muted flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-muted rounded w-1/4" />
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-10">
            <Icon name="AlertCircle" size={24} className="text-red-400 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        ) : activities?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Activity" size={32} className="text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm font-medium text-foreground mb-1">No activity found</p>
            <p className="text-xs text-muted-foreground">
              {hasFilters ? 'Try adjusting your filters.' : 'No activity has been recorded for this site yet.'}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(grouped)?.map(([dateLabel, items]) => (
              <div key={dateLabel}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{dateLabel}</span>
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground">{items?.length} event{items?.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="space-y-2">
                  {items?.map(activity => {
                    const config = ACTIVITY_CONFIG?.[activity?.activity_type] || ACTIVITY_CONFIG?.default;
                    return (
                      <div key={activity?.id} className={`flex gap-3 p-4 rounded-xl border ${config?.border} bg-card`}>
                        <div className={`flex-shrink-0 w-9 h-9 rounded-full ${config?.bg} flex items-center justify-center mt-0.5`}>
                          <Icon name={config?.icon} size={16} className={config?.color} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div>
                              <span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full ${config?.bg} ${config?.color} mb-1`}>
                                {config?.label}
                              </span>
                              <p className="text-sm font-semibold text-foreground leading-snug">{activity?.title}</p>
                            </div>
                            <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                              {formatRelativeTime(activity?.created_at)}
                            </span>
                          </div>
                          {activity?.description && (
                            <p className="text-xs text-muted-foreground leading-relaxed">{activity?.description}</p>
                          )}
                          {activity?.user?.full_name && (
                            <div className="flex items-center gap-1.5 mt-1.5">
                              <div className="w-4 h-4 rounded-full bg-accent/20 flex items-center justify-center">
                                <Icon name="User" size={9} className="text-accent" />
                              </div>
                              <span className="text-xs text-muted-foreground">{activity?.user?.full_name}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientActivityTimeline;
